<script setup lang="ts">
/**
 * `/account/confirm?action=restart&token=…`: the page the restart e-mail opens.
 * Spends the one-time token (the server sells the fleet and pays the return in
 * one transaction), then says plainly whether it worked, the link was invalid
 * or used, or something else failed. Shown only to a signed-in, verified
 * captain: `App.vue` holds the link until then.
 *
 * On `AuthShell`'s steel card like the verification page.
 */
import { onMounted, ref } from 'vue';

import { useI18n } from '@/i18n';
import { ApiError, api, type RestartPreview } from '@/net/api';
import { useSession } from '@/state/session';
import AuthShell from './AuthShell.vue';
import AuthStatus from './auth/AuthStatus.vue';
import GameIcon from './kit/GameIcon.vue';
import ActionButton from './panel/ActionButton.vue';

const props = defineProps<{ token: string }>();
const emit = defineEmits<{ done: [] }>();

const { t, n } = useI18n();
const session = useSession();

/** The server's code for a token that is wrong, expired, used or someone else's. */
const ACCOUNT_TOKEN_INVALID = 'ACCOUNT_TOKEN_INVALID';

type State = 'working' | 'done' | 'invalid' | 'failed';
const state = ref<State>('working');
const detail = ref('');
const result = ref<RestartPreview | null>(null);

onMounted(async () => {
  try {
    const r = await api.confirmRestart(props.token);
    result.value = r.preview;
    session.setBalances(r.wallet.credits, r.wallet.diamonds);
    state.value = 'done';
  } catch (err) {
    if (err instanceof ApiError && err.code === ACCOUNT_TOKEN_INVALID) state.value = 'invalid';
    else {
      state.value = 'failed';
      detail.value = err instanceof ApiError ? err.message : t('verify.unreachable');
    }
  }
});
</script>

<template>
  <AuthShell test-id="restart-confirm-screen" icon="anchor" :title="t('restartConfirm.title')">
    <AuthStatus v-if="state === 'working'" icon="hourglass" :title="t('restartConfirm.working')" />

    <AuthStatus v-else-if="state === 'done'" icon="medal" tone="good" :title="t('restartConfirm.done')" data-test="restart-done">
      <template v-if="result">{{ t('restartConfirm.back', { pct: result.returnPct, silver: n(result.returned) }) }}</template>
    </AuthStatus>

    <AuthStatus v-else-if="state === 'invalid'" icon="flame" :title="t('restartConfirm.failed')" data-test="restart-invalid">
      {{ t('restartConfirm.expired') }}
    </AuthStatus>

    <AuthStatus v-else icon="flame" :title="t('restartConfirm.failed')" data-test="restart-failed">{{ detail }}</AuthStatus>

    <ActionButton v-if="state !== 'working'" variant="primary" block size="lg" data-test="restart-continue" @click="emit('done')">
      <GameIcon name="anchor" :size="40" /> {{ t('restartConfirm.toHarbour') }}
    </ActionButton>
  </AuthShell>
</template>
